import {computedFn} from '@api'
import {CatalogInstanceChildStore, CatalogStore} from '@stores/catalog'
import {deployments} from '@stores/dev'
import {ElasticSearchId, HitsResult} from '@stores/elasticsearch'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {authStore, removeUndefinedsNested} from '@utils'
import {action, autorun, computed, observable, runInAction} from 'mobx'
import {IDisposer} from 'mobx-utils'

export type EsTagType = string

export interface TagRecord {
  _id?: ElasticSearchId
  name?: string
  type?: EsTagType
  description?: string
  updatedAt?: string
}

export class TagsStore extends CatalogInstanceChildStore {
  constructor(public catalog: CatalogStore) {
    super(catalog)

    synchronizeStores(() => {
      const {workspaces} = this

      this._toDispose.push(
        autorun(() => {
        if (authStore.userIsAuthenticated && workspaces.deploymentConfig) {
          this.hasLoadedTags = false
          this.loadTags()
        }
      }))
    })
  }

  _toDispose: IDisposer[] = []

  @action dispose = () => {
    this._toDispose.forEach(f => f())
  }

  esQueryForTagType = computedFn((tagType: string, searchText: string) => {
    const {es} = this
    return {
      query: {
        bool: {
          filter: [
            {
              term: {
                type: {
                  value: tagType
                }
              }
            },
            searchText !== '' && {
              query_string: {
                fields: ['name.text'],
                query: `*${es.tokenizeQueryString(searchText)}*`
              }
            }
          ].filter(_.identity)
        }
      }
    }
  }, {keepAlive: true})

  @observable tagsCache: { [query: string]: HitsResult } = {}

  loadTagsFromEs = async ({tagType, searchText, ...options}: { tagType: string, searchText: string, page?: number, pageSize?: number }, useCachedResult = false) => {
    const page = options.page ?? 0, pageSize = options.pageSize ?? 500,
      {tagsCache, es} = this

    const esQuery = this.esQueryForTagType(tagType, searchText)

    if (useCachedResult) {
      const cachedResult = this.tagsCache[JSON.stringify(esQuery)]
      if (cachedResult) return cachedResult
    }

    const {hits: {hits, total}} = await es.search({
      index: es.indexes.tag,
      meta: `SidebarChipRenderer.runQuery(page: ${page}, pageSize: ${pageSize})`,
      body: {
        ...esQuery,
        from: pageSize * page,
        size: pageSize,
        sort: [
          {createAt: {order: 'asc', unmapped_type: 'date'}}
        ]
      }
    })

    tagsCache[tagType] = {
      hits: [
        // ...(tagsCache?.[tagType]?.hits ?? []),
        ...hits
      ], total
    }

    return {hits, total}
  }

  private es_getTagById = async (id: string, _source = ['name', 'type', 'description']) => {
    const {es} = this
    return await es.get({index: es.indexes.tag, meta: `Get tag '${id}'`, id: id, _source})
  }

  @action tagById = async (id: string) => {
    /**
     * What happens when someone creates a new tag and associates an MPV to it and now it shows up in your workspace without you refreshing the page.
     *
     * The id will not be in our byId cache.
     *
     */

    const {_byId, es_getTagById} = this

    let r = this._byId[id]

    if (!r) {
      r = await es_getTagById(id)
      _byId[id] = r
    }

    return r
  }

  @observable _byId: { [id: string]: Partial<TagRecord> } = {}
  @observable hasLoadedTags = false

  @action loadTags = async () => {
    try {
      const {es} = this
      this.hasLoadedTags = false
      const {items, total} = es.unwrapHitsResult<TagRecord>(
        await es.search({
          index: es.indexes.tag,
          meta: `Load all tags for '${deployments.selectedDeploymentId}'`,
          body: {
            size: 10000
          }
        }))

      runInAction(() => {
        const {_byId} = this
        items.forEach(h => {
          _byId[h._id] = h
        })
      })
    } finally {
      this.hasLoadedTags = true
    }
  }
}

//
// export class TagRecordStore {
//   constructor(tag: TagRecord) {
//     this.record = tag
//   }
//
//   readonly record: TagRecord
// }



// WEBPACK FOOTER //
// ./src/stores/tags/TagsStore.ts